import { useCallback, useEffect, useMemo, useState } from 'react';
import { authApi, portailApi } from '../../api/resources';
import { useSession } from '../../api/session';
import { fmt } from '../../lib/currency';
import { GuideInstallation } from '../../pwa/GuideInstallation.jsx';
import { ETATS_COMMANDE } from '../../lib/etats-commande';
import { C } from '../../theme/palette';
import { NOM_LOGICIEL } from '../../lib/marque';

const carte = {
  background: '#fff',
  borderRadius: 14,
  padding: 14,
  marginBottom: 10,
  boxShadow: '0 1px 3px rgba(0,0,0,.06)',
};
const bouton = (fond, plein = true) => ({
  border: plein ? 'none' : '1.5px solid ' + fond,
  background: plein ? fond : '#fff',
  color: plein ? '#fff' : fond,
  borderRadius: 12,
  padding: '12px 14px',
  fontSize: 14,
  fontWeight: 800,
  cursor: 'pointer',
  fontFamily: 'inherit',
});
const champ = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid ' + C.border,
  borderRadius: 11,
  padding: '11px 12px',
  fontSize: 15,
  fontFamily: 'inherit',
};

/**
 * L'espace client du magasin : ce que voit une personne d'une entreprise
 * cliente, connectée avec son propre compte.
 *
 * <p>Trois onglets : « Commander » (le catalogue à ses prix, un panier, la
 * commande envoyée au magasin), « Mes commandes » (leur suivi, et
 * « Recommander ») et, pour le responsable, « Mon équipe » (inviter ses
 * employés, fermer un accès). Le client est vouvoyé.
 *
 * @returns {import('react').ReactElement} l'espace client
 */
export function PortailClient() {
  const { deconnexion, basculeSociete } = useSession();
  const [profil, setProfil] = useState(null);
  const [erreur, setErreur] = useState('');
  const [onglet, setOnglet] = useState('commander');
  const [catalogue, setCatalogue] = useState([]);
  const [panier, setPanier] = useState({});
  const [commandes, setCommandes] = useState(null);
  const [societes, setSocietes] = useState([]);

  useEffect(() => {
    portailApi
      .moi()
      .then(setProfil)
      .catch((e) => setErreur(e?.message || 'Votre espace client n’a pas pu s’ouvrir.'));
    portailApi
      .catalogue()
      .then(setCatalogue)
      .catch(() => {});
    authApi
      .societes()
      .then((l) => setSocietes(Array.isArray(l) ? l : []))
      .catch(() => {});
  }, []);

  const rechargeCommandes = useCallback(() => {
    portailApi
      .commandes()
      .then(setCommandes)
      .catch(() => setCommandes([]));
  }, []);
  useEffect(() => {
    if (onglet === 'commandes') rechargeCommandes();
  }, [onglet, rechargeCommandes]);

  const devise = profil?.magasin?.devise || 'CHF';
  const parReference = useMemo(
    () => Object.fromEntries(catalogue.map((a) => [a.reference, a])),
    [catalogue],
  );
  const lignesPanier = Object.entries(panier)
    .filter(([, q]) => q > 0)
    .map(([ref, q]) => ({ article: parReference[ref], quantite: q }))
    .filter((l) => l.article);
  const totalPanier = lignesPanier.reduce(
    (s, l) => s + l.quantite * Number(l.article.prixNet || 0),
    0,
  );
  const ajoute = (ref, delta) =>
    setPanier((p) => ({
      ...p,
      [ref]: Math.max(0, Math.round(((p[ref] || 0) + delta) * 1000) / 1000),
    }));
  const fixe = (ref, q) => setPanier((p) => ({ ...p, [ref]: Math.max(0, Number(q) || 0) }));
  const recommande = (commande) => {
    const p = {};
    (commande.lignes || []).forEach((l) => {
      if (l.reference && parReference[l.reference]) p[l.reference] = Number(l.quantite) || 1;
    });
    setPanier(p);
    setOnglet('panier');
  };

  if (erreur) {
    return (
      <div style={{ minHeight: '100vh', background: '#F3F4F6', padding: 20 }}>
        <div style={{ ...carte, textAlign: 'center' }}>
          <div style={{ fontSize: 30 }}>🔒</div>
          <p style={{ fontSize: 15 }}>{erreur}</p>
          <button style={bouton(C.navy)} onClick={deconnexion}>
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  const autres = societes.filter((s) => !s.courante);

  return (
    <div style={{ minHeight: '100vh', background: '#F3F4F6', paddingBottom: 96, color: C.ink }}>
      <header style={{ background: C.navy, color: '#fff', padding: '14px 16px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {profil?.magasin?.logo ? (
            <img
              src={profil.magasin.logo}
              alt=""
              style={{
                height: 34,
                maxWidth: 90,
                objectFit: 'contain',
                background: '#fff',
                borderRadius: 6,
                padding: 2,
              }}
            />
          ) : (
            <span style={{ fontSize: 24 }}>🏬</span>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 900 }}>{profil?.magasin?.nom || 'Magasin'}</div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>
              {profil
                ? profil.client + ' · ' + [profil.prenom, profil.nom].filter(Boolean).join(' ')
                : '…'}
            </div>
          </div>
          <button
            onClick={deconnexion}
            aria-label="Se déconnecter"
            style={{
              ...bouton('#fff', false),
              padding: '7px 10px',
              fontSize: 12,
              background: 'transparent',
              color: '#fff',
              border: '1px solid rgba(255,255,255,.4)',
            }}
          >
            Quitter
          </button>
        </div>
        {autres.length > 0 && (
          <select
            aria-label="Changer de société"
            value=""
            onChange={(e) => e.target.value && basculeSociete(Number(e.target.value))}
            style={{ marginTop: 10, width: '100%', borderRadius: 9, padding: 8, fontSize: 13 }}
          >
            <option value="">↔ Revenir à une autre de mes sociétés…</option>
            {autres.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nom}
              </option>
            ))}
          </select>
        )}
      </header>

      <nav
        style={{ display: 'flex', gap: 6, padding: '10px 12px 0', maxWidth: 640, margin: '0 auto' }}
      >
        {[
          ['commander', '🛒 Commander'],
          ['commandes', '📦 Mes commandes'],
          ...(profil?.responsable ? [['equipe', '👷 Mon équipe']] : []),
        ].map(([cle, lib]) => (
          <button
            key={cle}
            onClick={() => setOnglet(cle)}
            aria-pressed={onglet === cle || (cle === 'commander' && onglet === 'panier')}
            style={{
              ...bouton(C.navy, onglet === cle || (cle === 'commander' && onglet === 'panier')),
              // Trois onglets côte à côte sur un iPhone : l'icône au-dessus
              // du libellé, pour que « Mon équipe » ne sorte pas de l'écran.
              flex: '1 1 0',
              minWidth: 0,
              padding: '7px 4px',
              fontSize: 12.5,
              lineHeight: 1.25,
              whiteSpace: 'nowrap',
            }}
          >
            <span aria-hidden="true" style={{ display: 'block', fontSize: 17 }}>
              {lib.split(' ')[0]}
            </span>
            {lib.slice(lib.indexOf(' ') + 1)}
          </button>
        ))}
      </nav>

      <main style={{ padding: 12, maxWidth: 640, margin: '0 auto' }}>
        <GuideInstallation
          vous
          replie
          nom={profil?.magasin?.nom || NOM_LOGICIEL}
          apres="Vous commanderez en un geste."
        />
        {onglet === 'commander' && (
          <Catalogue
            catalogue={catalogue}
            panier={panier}
            ajoute={ajoute}
            fixe={fixe}
            devise={devise}
          />
        )}
        {onglet === 'panier' && (
          <Panier
            lignes={lignesPanier}
            total={totalPanier}
            devise={devise}
            fixe={fixe}
            magasin={profil?.magasin?.nom}
            retour={() => setOnglet('commander')}
            envoyee={() => {
              setPanier({});
            }}
            voirCommandes={() => setOnglet('commandes')}
          />
        )}
        {onglet === 'commandes' && (
          <MesCommandes
            commandes={commandes}
            devise={devise}
            responsable={!!profil?.responsable}
            recommande={recommande}
          />
        )}
        {onglet === 'equipe' && profil?.responsable && <MonEquipe client={profil.client} />}
      </main>

      {onglet === 'commander' && lignesPanier.length > 0 && (
        <div
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            padding: 12,
            background: 'linear-gradient(transparent, #F3F4F6 30%)',
          }}
        >
          <button
            onClick={() => setOnglet('panier')}
            style={{
              ...bouton(C.green),
              width: '100%',
              maxWidth: 640,
              display: 'block',
              margin: '0 auto',
              fontSize: 16,
              padding: '15px 14px',
            }}
          >
            🛒 {lignesPanier.length} article(s) · {devise} {fmt(totalPanier)} HT — Voir le panier ›
          </button>
        </div>
      )}
    </div>
  );
}

/** Le catalogue à vos prix, par rayon, avec la recherche. */
function Catalogue({ catalogue, panier, ajoute, fixe, devise }) {
  const [recherche, setRecherche] = useState('');
  const [rayon, setRayon] = useState('');
  const filtre = recherche.trim().toLowerCase();
  const rayons = Array.from(new Set(catalogue.map((a) => a.categorie || 'Divers')));
  const visibles = catalogue.filter(
    (a) =>
      (!rayon || (a.categorie || 'Divers') === rayon) &&
      (!filtre ||
        (a.nom || '').toLowerCase().includes(filtre) ||
        (a.reference || '').toLowerCase().includes(filtre) ||
        (a.categorie || '').toLowerCase().includes(filtre)),
  );
  return (
    <>
      <input
        style={{ ...champ, marginBottom: 8 }}
        placeholder="🔎 Rechercher un article, une référence…"
        value={recherche}
        onChange={(e) => setRecherche(e.target.value)}
      />
      {rayons.length > 1 && (
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 10 }}>
          {['', ...rayons].map((r) => (
            <button
              key={r || 'tous'}
              onClick={() => setRayon(r)}
              style={{
                ...bouton(C.teal, rayon === r),
                padding: '6px 11px',
                fontSize: 12,
                whiteSpace: 'nowrap',
              }}
            >
              {r || 'Tout'}
            </button>
          ))}
        </div>
      )}
      {visibles.length === 0 && (
        <div style={{ ...carte, textAlign: 'center', color: C.muted }}>
          Aucun article ne correspond.
        </div>
      )}
      {visibles.map((a) => {
        const q = panier[a.reference] || 0;
        const remise = Number(a.remise || 0);
        return (
          <div
            key={a.reference}
            style={{ ...carte, display: 'flex', gap: 12, alignItems: 'center' }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 10,
                background: '#F1F5F9',
                flexShrink: 0,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 26,
              }}
            >
              {a.image ? (
                <img
                  src={portailApi.image(a.id)}
                  alt=""
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                '📦'
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.3 }}>{a.nom}</div>
              <div style={{ fontSize: 11.5, color: C.muted }}>
                {a.reference} · {a.unite || 'pce'}
                {!a.disponible && ' · sur commande'}
              </div>
              <div style={{ marginTop: 3 }}>
                {remise > 0 && (
                  <span
                    style={{
                      fontSize: 12,
                      color: C.muted,
                      textDecoration: 'line-through',
                      marginRight: 6,
                    }}
                  >
                    {fmt(a.prixPublic)}
                  </span>
                )}
                <span style={{ fontSize: 15, fontWeight: 900, color: C.navy }}>
                  {devise} {fmt(a.prixNet)}
                </span>
                <span style={{ fontSize: 11, color: C.muted }}> HT</span>
                {remise > 0 && (
                  <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 800, color: C.green }}>
                    −{remise} %
                  </span>
                )}
              </div>
            </div>
            {q > 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button
                  aria-label={'Moins ' + a.nom}
                  onClick={() => ajoute(a.reference, -1)}
                  style={{ ...bouton(C.navy, false), padding: '6px 11px' }}
                >
                  −
                </button>
                <input
                  aria-label={'Quantité ' + a.nom}
                  inputMode="decimal"
                  value={q}
                  onChange={(e) => fixe(a.reference, e.target.value.replace(',', '.'))}
                  style={{
                    width: 42,
                    textAlign: 'center',
                    border: '1px solid ' + C.border,
                    borderRadius: 8,
                    padding: 6,
                    fontSize: 15,
                    fontWeight: 800,
                  }}
                />
                <button
                  aria-label={'Plus ' + a.nom}
                  onClick={() => ajoute(a.reference, 1)}
                  style={{ ...bouton(C.navy), padding: '6px 11px' }}
                >
                  +
                </button>
              </div>
            ) : (
              <button
                aria-label={'Ajouter ' + a.nom}
                onClick={() => ajoute(a.reference, 1)}
                style={{ ...bouton(C.green), padding: '10px 13px' }}
              >
                ＋
              </button>
            )}
          </div>
        );
      })}
    </>
  );
}

/** Le panier : les quantités, l'adresse de livraison, l'envoi. */
function Panier({ lignes, total, devise, fixe, magasin, retour, envoyee, voirCommandes }) {
  const [adresse, setAdresse] = useState('');
  const [envoi, setEnvoi] = useState({ enCours: false, erreur: '' });
  const [recu, setRecu] = useState(null);
  const envoie = async () => {
    setEnvoi({ enCours: true, erreur: '' });
    try {
      const r = await portailApi.commande({
        adresseLivraison: adresse.trim() || null,
        lignes: lignes.map((l) => ({ reference: l.article.reference, quantite: l.quantite })),
      });
      setRecu(r);
      envoyee();
      setEnvoi({ enCours: false, erreur: '' });
    } catch (e) {
      setEnvoi({
        enCours: false,
        erreur: e?.message || 'La commande n’est pas partie. Réessayez.',
      });
    }
  };
  if (recu) {
    return (
      <div style={{ ...carte, textAlign: 'center' }}>
        <div style={{ fontSize: 40 }}>✅</div>
        <h2 style={{ fontSize: 19, margin: '6px 0' }}>Commande {recu.numero} envoyée</h2>
        <p style={{ fontSize: 14, color: C.grey, lineHeight: 1.5 }}>
          {magasin || 'Le magasin'} l’a reçue et vous la confirmera. Total {devise}{' '}
          {fmt(recu.total)} HT.
        </p>
        <button style={{ ...bouton(C.navy), width: '100%' }} onClick={voirCommandes}>
          📦 Suivre mes commandes
        </button>
      </div>
    );
  }
  return (
    <>
      <button onClick={retour} style={{ ...bouton(C.navy, false), marginBottom: 10 }}>
        ‹ Continuer mes achats
      </button>
      <div style={carte}>
        <h2 style={{ fontSize: 17, margin: '0 0 8px' }}>Votre panier</h2>
        {lignes.length === 0 && <div style={{ color: C.muted }}>Le panier est vide.</div>}
        {lignes.map((l) => (
          <div
            key={l.article.reference}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 0',
              borderBottom: '1px solid ' + C.border,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{l.article.nom}</div>
              <div style={{ fontSize: 12, color: C.muted }}>
                {devise} {fmt(l.article.prixNet)} / {l.article.unite || 'pce'}
              </div>
            </div>
            <input
              aria-label={'Quantité ' + l.article.nom}
              inputMode="decimal"
              value={l.quantite}
              onChange={(e) => fixe(l.article.reference, e.target.value.replace(',', '.'))}
              style={{
                width: 52,
                textAlign: 'center',
                border: '1px solid ' + C.border,
                borderRadius: 8,
                padding: 7,
                fontSize: 15,
              }}
            />
            <div style={{ width: 82, textAlign: 'right', fontWeight: 800, fontSize: 14 }}>
              {fmt(l.quantite * Number(l.article.prixNet || 0))}
            </div>
            <button
              aria-label={'Retirer ' + l.article.nom}
              onClick={() => fixe(l.article.reference, 0)}
              style={{
                border: 'none',
                background: 'none',
                fontSize: 18,
                cursor: 'pointer',
                color: C.red,
              }}
            >
              ✕
            </button>
          </div>
        ))}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 10,
            fontSize: 17,
            fontWeight: 900,
          }}
        >
          <span>Total hors TVA</span>
          <span>
            {devise} {fmt(total)}
          </span>
        </div>
        <input
          style={{ ...champ, marginTop: 12 }}
          placeholder="Adresse de livraison ou chantier (si différente)"
          value={adresse}
          onChange={(e) => setAdresse(e.target.value)}
        />
        {envoi.erreur && (
          <div role="alert" style={{ color: C.red, fontWeight: 700, fontSize: 13, marginTop: 8 }}>
            {envoi.erreur}
          </div>
        )}
        <button
          disabled={envoi.enCours || lignes.length === 0}
          onClick={envoie}
          style={{
            ...bouton(C.green),
            width: '100%',
            marginTop: 12,
            fontSize: 16,
            padding: '15px 14px',
            opacity: lignes.length === 0 ? 0.5 : 1,
          }}
        >
          {envoi.enCours ? 'Envoi…' : '✓ Envoyer la commande'}
        </button>
      </div>
    </>
  );
}

/** Le suivi des commandes, et « Recommander ». */
function MesCommandes({ commandes, devise, responsable, recommande }) {
  const [ouverte, setOuverte] = useState(null);
  if (commandes === null) return <div style={{ ...carte, color: C.muted }}>Chargement…</div>;
  if (commandes.length === 0)
    return (
      <div style={{ ...carte, textAlign: 'center', color: C.muted }}>
        Aucune commande pour l’instant.
      </div>
    );
  return commandes.map((c) => {
    const etat = ETATS_COMMANDE[c.statut] || { texte: c.statut, couleur: C.grey };
    return (
      <div key={c.id} style={carte}>
        <div
          role="button"
          onClick={() => setOuverte(ouverte === c.id ? null : c.id)}
          style={{ cursor: 'pointer' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 900 }}>{c.numero}</div>
            <div style={{ fontSize: 15, fontWeight: 900 }}>
              {devise} {fmt(c.total)}
            </div>
          </div>
          <div style={{ fontSize: 12, color: C.muted }}>
            {c.date ? c.date.split('-').reverse().join('.') : ''}
            {responsable && c.demandeur ? ' · par ' + c.demandeur : ''} · {(c.lignes || []).length}{' '}
            article(s)
          </div>
          <div style={{ marginTop: 6, fontSize: 13, fontWeight: 800, color: etat.couleur }}>
            ● {etat.texte}
          </div>
        </div>
        {ouverte === c.id && (
          <div style={{ marginTop: 8, borderTop: '1px solid ' + C.border, paddingTop: 8 }}>
            {(c.lignes || []).map((l, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 13,
                  padding: '3px 0',
                }}
              >
                <span>
                  {Number(l.quantite)} {l.unite} · {l.designation}
                </span>
                <span>{fmt(Number(l.prixNet) * Number(l.quantite))}</span>
              </div>
            ))}
            <button
              onClick={() => recommande(c)}
              style={{ ...bouton(C.teal), width: '100%', marginTop: 8 }}
            >
              🔁 Recommander les mêmes articles
            </button>
          </div>
        )}
      </div>
    );
  });
}

/** L'équipe du client : inviter un employé, fermer un accès. Au responsable. */
function MonEquipe({ client }) {
  const [personnes, setPersonnes] = useState(null);
  const [f, setF] = useState({ prenom: '', nom: '', email: '' });
  const [invitee, setInvitee] = useState(null);
  const [erreur, setErreur] = useState('');
  const recharge = () =>
    portailApi
      .equipe()
      .then(setPersonnes)
      .catch(() => setPersonnes([]));
  useEffect(() => {
    recharge();
  }, []);
  const invite = async (e) => {
    e.preventDefault();
    if (!f.prenom.trim()) return setErreur('Indiquez au moins le prénom.');
    setErreur('');
    try {
      const r = await portailApi.invite({
        prenom: f.prenom.trim(),
        nom: f.nom.trim() || null,
        email: f.email.trim() || null,
      });
      setInvitee(r);
      setF({ prenom: '', nom: '', email: '' });
      recharge();
    } catch (err) {
      setErreur(err?.message || 'L’invitation n’est pas partie.');
    }
  };
  const etats = {
    ACTIF: '✓ Accès ouvert',
    FERME: '🔒 Accès fermé',
    INVITE: '⏳ Invité',
    EXPIREE: '⌛ Lien expiré',
  };
  return (
    <>
      <form style={carte} onSubmit={invite}>
        <h2 style={{ fontSize: 17, margin: '0 0 4px' }}>Inviter un employé</h2>
        <p style={{ fontSize: 13, color: C.grey, margin: '0 0 8px', lineHeight: 1.45 }}>
          Il reçoit un lien, crée son compte (nom, prénom, mot de passe) et commande aux prix de{' '}
          {client}.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <input
            style={champ}
            placeholder="Prénom"
            value={f.prenom}
            onChange={(e) => setF({ ...f, prenom: e.target.value })}
          />
          <input
            style={champ}
            placeholder="Nom"
            value={f.nom}
            onChange={(e) => setF({ ...f, nom: e.target.value })}
          />
        </div>
        <input
          style={{ ...champ, marginTop: 8 }}
          type="email"
          placeholder="E-mail (facultatif : sinon envoyez le lien par WhatsApp)"
          value={f.email}
          onChange={(e) => setF({ ...f, email: e.target.value })}
        />
        {erreur && (
          <div role="alert" style={{ color: C.red, fontWeight: 700, fontSize: 13, marginTop: 8 }}>
            {erreur}
          </div>
        )}
        <button type="submit" style={{ ...bouton(C.green), width: '100%', marginTop: 10 }}>
          ✉️ Inviter
        </button>
      </form>
      {invitee && <PartageInvitation invitee={invitee} />}
      <div style={carte}>
        <h2 style={{ fontSize: 17, margin: '0 0 6px' }}>Votre équipe</h2>
        {personnes === null && <div style={{ color: C.muted }}>Chargement…</div>}
        {(personnes || []).map((p) => (
          <div
            key={(p.invitation ? 'i' : 'c') + p.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 0',
              borderBottom: '1px solid ' + C.border,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800 }}>
                {[p.prenom, p.nom].filter(Boolean).join(' ')}
                {p.role === 'RESPONSABLE' && (
                  <span style={{ fontSize: 11, color: C.teal }}> · responsable</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: C.muted }}>
                {p.email || '—'} · {etats[p.etat] || p.etat}
              </div>
            </div>
            {p.invitation && p.etat === 'INVITE' && (
              <button
                onClick={() => portailApi.annuleInvitation(p.id).then(recharge)}
                style={{ ...bouton(C.red, false), padding: '6px 9px', fontSize: 12 }}
              >
                Annuler
              </button>
            )}
            {!p.invitation && p.role !== 'RESPONSABLE' && (
              <button
                onClick={() => portailApi.acces(p.id, p.etat !== 'ACTIF').then(recharge)}
                style={{
                  ...bouton(p.etat === 'ACTIF' ? C.red : C.green, false),
                  padding: '6px 9px',
                  fontSize: 12,
                }}
              >
                {p.etat === 'ACTIF' ? 'Fermer l’accès' : 'Rouvrir'}
              </button>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

/**
 * Le lien d'une invitation tout juste créée, à envoyer par WhatsApp ou à
 * copier ; parti par e-mail si une adresse a été donnée.
 *
 * @param {object} props propriétés
 * @param {object} props.invitee l'invitation créée
 * @returns {import('react').ReactElement} la carte de partage
 */
export function PartageInvitation({ invitee }) {
  const [copie, setCopie] = useState(false);
  return (
    <div style={{ ...carte, border: '1.5px solid ' + C.green }}>
      <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>
        ✓ Invitation prête pour {invitee.personne?.prenom}
        {invitee.courrielEnvoye ? ' — envoyée par e-mail' : ''}
      </div>
      <div style={{ fontSize: 12, color: C.muted, wordBreak: 'break-all', marginBottom: 8 }}>
        {invitee.lien}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <a
          href={'https://wa.me/?text=' + encodeURIComponent(invitee.message)}
          target="_blank"
          rel="noreferrer"
          style={{ ...bouton(C.green), flex: 1, textAlign: 'center', textDecoration: 'none' }}
        >
          💬 WhatsApp
        </a>
        <button
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(invitee.message);
              setCopie(true);
            } catch {
              setCopie(false);
            }
          }}
          style={{ ...bouton(C.navy, false), flex: 1 }}
        >
          {copie ? '✓ Copié' : '📋 Copier'}
        </button>
      </div>
    </div>
  );
}
